import React from 'react';
import { AuthContext } from '../util/context';
import { moveItemOnTree } from '@atlaskit/tree';

import { MUTATION } from '../gql-operations';

import { useMutation } from '@apollo/client';

import useMoveFolderMutation from '../hooks/useMoveFolderMutation';
import useAddFoldersToTree from './useAddFoldersToTree';
import useGetSubfolders from './useGetSubfolders';
import customAnalytics from '../util/custom-analytics';

const logFolderMove = (event, details) => {
    if (process.env.NODE_ENV !== 'production') {
        console.debug(`[FolderMove tree] ${event}`, details);
    }
};

function useUpdateFoldersOnTree() {
    const [createFolder] = useMutation(MUTATION.CREATE_FOLDER, {
        onCompleted: () => {
            // analytics below
        }
    });
    const [moveFolder] = useMoveFolderMutation();
    const [addChildrenToFolder] = useAddFoldersToTree();
    const [getSubfolders] = useGetSubfolders();

    const {
        state: { treeData },
        context: { setNewTree, setSelectedFolder }
    } = React.useContext(AuthContext);

    const moveFolderToRoot = async folder => {
        // console.log('this is the folder item', folder);
        // ovde treba da stigne update
        let response = await moveFolder(folder, null);
        // console.log('dkdkdkkdd', response);
        // since it's moving to the root, only the first folder should be updated
        let updatedFolder = response.data.moveFolder[0];
        removeFolderFromTreeManually(folder, updatedFolder);
        addChildrenToFolder([updatedFolder]);
        setSelectedFolder(updatedFolder);
        customAnalytics.folder_edit({
            'action': "Move",
            "destination": "Root"
        })
        return updatedFolder;
    };

    const createAndMoveFolder = async (newTitle, folderToMove) => {
        let response = await createFolder({
            variables: {
                data: {
                    title: newTitle,
                    parent: folderToMove.parent
                }
            }
        });
        if (folderToMove.parent) {
            customAnalytics.create_subfolder_from_folder('Create and Move');
        } else {
            customAnalytics.create_home_folder('Create and Move');
        }
        // console.log('created', response);
        if (response) {
            let createdFolder = response.data.createFolder;
            // console.log('created folder', createdFolder);
            let response = await moveFolder(folderToMove, createdFolder);
            // console.log('response from moving', response);
            let updatedFolder = response.data.moveFolder[0];
            updateTreeAfterCreated(folderToMove);
            // selecting the updated moved folder
            setSelectedFolder(updatedFolder);
            // getting the subfolder for the newly created
            getSubfolders(createdFolder, true);
            // returning created so we can scroll to him
            customAnalytics.folder_edit({
                'action': "Move",
                "destination": createdFolder && createdFolder.parent ? "Subfolder" : "HomeFolder"
            }, folderToMove)
            return createdFolder;
        }
    };

    const updateTreeAfterCreated = async folder => {
        if (folder) {
            let newTreeCopy = { ...treeData };

            // updating the folder to move with updated one
            const origFolder = newTreeCopy.items[folder.id];

            if (origFolder) {
                const parent = newTreeCopy.items[folder.parent ? folder.parent : 'root'];
                if (parent) {
                    let newSourceChildren = [...parent.children];
                    const folderIndex = newSourceChildren.findIndex(obj => obj === origFolder.id);
                    if (folderIndex > -1) {
                        newSourceChildren.splice(folderIndex, 1);
                    }

                    // updating the new parent
                    parent.children = newSourceChildren;
                    newTreeCopy.items[parent.id] = parent;
                    // updated automatically on tree

                    // setNewTree(newTreeCopy);
                }
            }
        }
    };

    // removing the folder from the treeData manually
    const removeFolderFromTreeManually = async (folder, updatedFolder) => {
        if (folder) {
            let newTreeCopy = { ...treeData };

            // updating the folder to move with updated one
            const origFolder = newTreeCopy.items[folder.id];
            if (origFolder) {
                origFolder.data = updatedFolder;

                // getting the parent to remove it from children
                const parent = newTreeCopy.items[folder.parent];
                if (parent) {
                    let newSourceChildren = [...parent.children];
                    const folderIndex = newSourceChildren.findIndex(obj => obj === origFolder.id);
                    if (folderIndex > -1) {
                        newSourceChildren.splice(folderIndex, 1);
                    }

                    // updating the new parent
                    parent.children = newSourceChildren;
                    newTreeCopy.items[parent.id] = parent;

                    setNewTree(newTreeCopy);
                }
            }
        }
    };

    const updateFoldersOnTree = async (source, destination) => {
        if (!source?.id || !destination?.id || source.id === destination.id) {
            logFolderMove('skipped invalid move', { sourceId: source?.id, destinationId: destination?.id });
            return;
        }

        logFolderMove('started', {
            sourceId: source.id,
            sourceParentId: source.parent || null,
            destinationId: destination.id,
            treeHasSource: Boolean(treeData.items[source.id]),
            treeHasDestination: Boolean(treeData.items[destination.id])
        });
        let folderToMove = { id: source.id };
        let response = await moveFolder(folderToMove, { id: destination.id });
        let foldersToUpdate = response?.data?.moveFolder?.filter(Boolean) || [];
        let movedFolder = foldersToUpdate.find(folder => folder.id === source.id);

        if (!movedFolder) {
            logFolderMove('mutation response omitted moved folder', { sourceId: source.id, foldersToUpdate });
            return;
        }

        const parentId = source.parent || 'root';
        const sourceParent = treeData.items[parentId];
        const sourceIndex = sourceParent?.children?.findIndex(id => id === source.id);
        const destinationParent = treeData.items[destination.id];
        logFolderMove('applying response', {
            serverParentId: movedFolder.parent || null,
            sourceParentId: parentId,
            sourceIndex,
            destinationId: destination.id,
            destinationChildCount: destinationParent?.children?.length
        });

        if (sourceIndex > -1 && destinationParent) {
            // Atlaskit's destination requires an index. Omitting it made the
            // search-dropdown move dependent on an implementation detail and
            // could leave the tree unchanged after a successful mutation.
            const newTree = moveItemOnTree(
                treeData,
                { parentId, index: sourceIndex },
                { parentId: destination.id, index: destinationParent.children?.length || 0 }
            );

            foldersToUpdate.forEach(incomingFolder => {
                if (newTree.items[incomingFolder.id]) {
                    newTree.items[incomingFolder.id].data = { ...incomingFolder };
                }
            });
            setNewTree(newTree);
            logFolderMove('updated loaded destination branch', { sourceId: source.id, destinationId: destination.id });
        } else if (sourceIndex > -1) {
            // Search can select a folder whose branch has not been expanded in
            // the sidebar. Remove the moved item from its visible old parent;
            // its destination branch will be populated when it is expanded.
            const newTree = {
                ...treeData,
                items: {
                    ...treeData.items,
                    [parentId]: {
                        ...sourceParent,
                        children: sourceParent.children.filter(id => id !== source.id)
                    },
                    [source.id]: {
                        ...treeData.items[source.id],
                        data: { ...movedFolder }
                    }
                }
            };
            setNewTree(newTree);
            logFolderMove('removed from source; destination branch is unloaded', {
                sourceId: source.id,
                destinationId: destination.id
            });
        } else {
            addChildrenToFolder([movedFolder]);
            logFolderMove('source absent from tree; added returned folder', {
                sourceId: source.id,
                destinationId: destination.id
            });
        }

        customAnalytics.folder_edit({
            'action': "Move",
            "destination": destination.parent ? "Subfolder" : "HomeFolder"
        }, source)
    };

    return { updateFoldersOnTree, moveFolderToRoot, createAndMoveFolder };
}

export default useUpdateFoldersOnTree;
